// Cooking-intent cascade — curated → derived → bridge.
//
// Vague mood/occasion/aspiration asks ("michelin star", "casual date
// night chicken", "rainy day food", "sick food") carry cooking INTENT:
// effort, refinement, serving count, occasion, mood. The generator
// throws that away today. This module resolves a typed prompt (+ the
// active concept frame) into one normalized `intent` object that does
// two jobs downstream:
//   1. shapes the draft  — execution directives + a starting serves
//   2. organizes the book — canonical `tags` stamped on the recipe
//
// The cascade mirrors the app's curated/derived/bridge pattern
// (CLAUDE.md "Classification weights"; cf. brandExpertise.js):
//   • CURATED  — COOKING_INTENTS, a hand-authored phrase → intent map.
//   • DERIVED  — the Haiku classifier's `intent` block (passed in as
//                `derivedIntent`), for the long tail curated misses.
//   • BRIDGE   — conceptFrames.js VIBES → intent, so dream-mode vibes
//                also set serves/effort/tags.
// Precedence is per-axis curated ?? derived ?? bridge; `directives`
// and `tags` are UNIONED (additive guidance, not exclusive claims).
//
// SCOPE — load-bearing (Protect dream mode): intent describes HOW and
// FOR WHOM to cook (execution, portion, occasion/mood). It NEVER
// carries cuisine, dish, or ingredient — mergeIntents only ever reads
// the execution axes below, so a stray cuisine/dish field from the
// model can't leak through.

import { clampServings } from "../lib/recipeConstants";

const EFFORTS = new Set(["low", "moderate", "high"]);
const REFINEMENTS = new Set(["rustic", "everyday", "refined", "restaurant"]);
const AXES = ["effort", "refinement", "serves", "occasion", "mood"];

// ── Curated tier ─────────────────────────────────────────────────────
// Each entry: `match[]` surface phrases to scan for, the intent axes,
// and `tags[]` (canonical lowercase-hyphen slugs that read well as
// Recipe Book filter chips).
export const COOKING_INTENTS = {
  date_night: {
    match: ["casual date night", "date night", "date-night", "romantic dinner", "romantic meal"],
    effort: "moderate", refinement: "refined", serves: 2,
    occasion: "a relaxed date-night dinner for two",
    mood: "intimate, a little special but not fussy",
    directives: ["Plate for two with intention — one shared showpiece beats three rushed dishes."],
    tags: ["date-night"],
  },
  michelin: {
    match: ["michelin star", "michelin-star", "michelin", "fine dining", "tasting menu"],
    effort: "high", refinement: "restaurant", serves: 2,
    occasion: "a fine-dining plate",
    mood: "precise, elegant, restaurant-grade",
    directives: ["Restaurant-level execution: layered components, a real sauce, deliberate plating, refined technique — all achievable in a home kitchen."],
    tags: ["fancy", "date-night"],
  },
  impress: {
    match: ["to impress", "impress my friends", "impress my", "wow my", "dinner party", "showstopper", "show off", "showing off"],
    effort: "high", refinement: "refined", serves: 4,
    occasion: "a dish to impress guests",
    mood: "confident, generous, conversation-worthy",
    directives: ["Build in one memorable moment — a sauce, a sear, a garnish — that guests will talk about."],
    tags: ["fancy", "crowd-pleaser"],
  },
  rainy_day: {
    match: ["rainy day", "rainy-day", "cozy", "cosy", "comfort food", "comforting", "cold day"],
    effort: "low", refinement: "rustic", serves: 2,
    occasion: "a cozy stay-in meal",
    mood: "warm, comforting, low-effort",
    directives: ["Lean into warmth and richness; one-pot or hands-off methods are welcome."],
    tags: ["comfort", "cozy"],
  },
  sick: {
    match: ["sick food", "under the weather", "feeling sick", "when im sick", "feeling unwell", "head cold", "the flu", "sick"],
    effort: "low", refinement: "everyday", serves: 2,
    occasion: "a restorative meal while unwell",
    mood: "gentle, nourishing, easy on the stomach",
    directives: ["Gentle and soothing: brothy or soft textures, mild seasoning, hydrating, easy to digest."],
    tags: ["restorative", "comfort"],
  },
  weeknight: {
    match: ["weeknight", "quick dinner", "fast dinner", "busy night", "quick weeknight", "easy weeknight"],
    effort: "low", refinement: "everyday", serves: 4,
    occasion: "a fast weeknight dinner",
    mood: "efficient, no-fuss",
    directives: ["Minimize active time and cleanup — aim for ~30 minutes and few pans."],
    tags: ["quick", "weeknight"],
  },
  meal_prep: {
    match: ["meal prep", "meal-prep", "batch cook", "batch cooking", "make ahead", "make-ahead", "lunch prep", "weekly prep"],
    effort: "moderate", refinement: "everyday", serves: 6,
    occasion: "batch-cooked meals for the week",
    mood: "practical, scalable, reheats well",
    directives: ["Scale up and favor dishes that hold and reheat without degrading."],
    tags: ["meal-prep"],
  },
};

// ── Bridge tier ──────────────────────────────────────────────────────
// conceptFrames.js VIBES id → intent. Reuses the VIBES ids verbatim so
// dream-mode vibes finally set serves/effort/tags. Vibes with no
// portion/effort signal (e.g. surprising_savory_sweet) are omitted.
const VIBE_INTENTS = {
  solo:             { serves: 1, occasion: "a solo meal for one", tags: ["solo"] },
  two_person_plate: { serves: 2, refinement: "refined", occasion: "an intimate two-person plate", mood: "intimate", tags: ["date-night"] },
  showstopper:      { effort: "high", refinement: "refined", serves: 4, occasion: "a dinner-party showstopper", mood: "confident, generous", tags: ["fancy", "crowd-pleaser"] },
  sunday_project:   { effort: "high", refinement: "rustic", occasion: "a leisurely weekend cooking project", mood: "ambitious, hands-on", tags: ["project"] },
  thunderstorm:     { effort: "low", refinement: "rustic", serves: 2, occasion: "a cozy stay-in meal", mood: "warm, comforting", tags: ["comfort", "cozy"] },
  cold_snap:        { effort: "low", refinement: "rustic", serves: 2, occasion: "a warming cold-day meal", mood: "warm, hearty", tags: ["comfort", "cozy"] },
  hangover:         { effort: "low", refinement: "everyday", serves: 1, occasion: "a restorative morning-after meal", mood: "restorative, satisfying", tags: ["restorative", "comfort"] },
  make_ahead:       { effort: "moderate", refinement: "everyday", serves: 6, occasion: "make-ahead meals for the week", mood: "practical, reheats well", tags: ["meal-prep"] },
  late_night:       { effort: "low", serves: 2, occasion: "a late-night bite", mood: "quick, satisfying", tags: ["quick"] },
  kid_friendly:     { refinement: "everyday", serves: 4, occasion: "a kid-friendly meal", tags: ["kid-friendly"] },
  picnic:           { serves: 4, occasion: "picnic-friendly food", mood: "portable, make-ahead", tags: ["picnic", "make-ahead"] },
  heat_wave:        { effort: "low", occasion: "a cool, no-cook-leaning meal for hot weather", mood: "light, cooling", tags: ["no-cook"] },
};

// ── Helpers ──────────────────────────────────────────────────────────
function norm(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}
function slug(s) {
  return norm(s).replace(/ /g, "-");
}
function pick(...vals) {
  for (const v of vals) if (v != null && v !== "") return v;
  return undefined;
}
function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}
function cloneIntent(src) {
  if (!src) return null;
  const { match, ...rest } = src;     // drop the curated `match` field
  return {
    ...rest,
    directives: Array.isArray(rest.directives) ? [...rest.directives] : [],
    tags: Array.isArray(rest.tags) ? [...rest.tags] : [],
  };
}

// CURATED tier: longest phrase match wins (so "casual date night"
// beats "date night" beats a bare token). Word-boundaried to avoid
// substring bleed ("sick" must be the word, not inside "basics").
export function intentFromPhrase(mealPrompt) {
  const hay = ` ${norm(mealPrompt)} `;
  if (hay.trim().length === 0) return null;
  let best = null;
  let bestLen = 0;
  for (const key of Object.keys(COOKING_INTENTS)) {
    const entry = COOKING_INTENTS[key];
    for (const phrase of entry.match) {
      const p = norm(phrase);
      if (!p) continue;
      if (hay.includes(` ${p} `) && p.length > bestLen) {
        best = entry;
        bestLen = p.length;
      }
    }
  }
  return cloneIntent(best);
}

// BRIDGE tier: concept-frame vibe → intent.
export function intentFromVibe(conceptFrame) {
  const id = conceptFrame?.vibe?.id;
  if (!id) return null;
  return cloneIntent(VIBE_INTENTS[id] || null);
}

// Merge the three tiers, curated > derived > bridge, per-axis. Only the
// execution axes (effort/refinement/serves/occasion/mood) + directives
// + tags are ever read, so cuisine/dish can't leak in from any tier.
export function mergeIntents(curated, derived, bridge) {
  const c = curated || {};
  const d = derived || {};
  const b = bridge || {};
  const out = {};
  for (const axis of AXES) {
    const v = pick(c[axis], d[axis], b[axis]);
    if (v !== undefined) out[axis] = v;
  }
  if (out.effort && !EFFORTS.has(out.effort)) delete out.effort;
  if (out.refinement && !REFINEMENTS.has(out.refinement)) delete out.refinement;
  if (out.serves != null) {
    const n = Math.round(Number(out.serves));
    // Same ceiling as every servings stepper (recipeConstants
    // MAX_SERVINGS) — this used to clamp intent metadata to 12 while
    // the UI allowed 100, silently shrinking crowd-cook intents.
    if (Number.isFinite(n)) out.serves = clampServings(n);
    else delete out.serves;
  }
  if (typeof out.occasion === "string") out.occasion = out.occasion.slice(0, 120);
  if (typeof out.mood === "string") out.mood = out.mood.slice(0, 120);

  const directives = uniq([
    ...(c.directives || []), ...(d.directives || []), ...(b.directives || []),
  ]).slice(0, 4);
  const tags = uniq([
    ...(c.tags || []), ...(d.tags || []), ...(b.tags || []),
  ].map(slug)).slice(0, 6);
  if (directives.length) out.directives = directives;
  if (tags.length) out.tags = tags;
  return out;
}

// THE single entry point. Returns the merged intent, or null when all
// three tiers are empty (so callers can guard with a simple truthiness
// check and emit nothing for plain dish prompts).
export function resolveCookingIntent({ mealPrompt = "", derivedIntent = null, conceptFrame = null } = {}) {
  const merged = mergeIntents(
    intentFromPhrase(mealPrompt),
    derivedIntent,
    intentFromVibe(conceptFrame),
  );
  return Object.keys(merged).length ? merged : null;
}
